import { twMerge } from "tailwind-merge";
import { useNewsCardClick } from "../News/components/useNewsCardClick";
import BFLImage from "@repo/shared-ui/BFLImage";
import { formatTimestamp } from "../../utils/helper";

interface LatestFinancialCardProps {
  id: string;
  logo: string;
  fallbackImage?: string;
  heading: string;
  symbol: string;
  perChange: number;
  createdAt: string;
  className?: string;
  componentPosition?: string;
  symbolClassName?: string;
  paragraphClassName?: string;
  onCardClick?: () => void;
}

const LatestFinancialCard = ({
  id,
  logo,
  fallbackImage = "https://cms-assets.bajajfinserv.in/is/image/bajajfinancestage/stocksplaceholder",
  heading,
  symbol,
  perChange,
  createdAt,
  className,
  componentPosition,
  symbolClassName,
  paragraphClassName,
  onCardClick,
}: LatestFinancialCardProps) => {
  const isPositive = perChange > 0;
  const resolvedLogoSrc = logo?.trim() ? logo : fallbackImage;
  const timeLabels = {
    justNow: "Just now",
    minutesAgo: "m ago",
    hoursAgo: "h ago",
    daysAgo: "d ago",
  };

  const { handleClick } = useNewsCardClick({
    newsId: id,
    newsHeading: heading,
    newsType: "financials",
    componentPosition: componentPosition ?? symbol ?? id,
  });

  return (
    <div
      className={twMerge(
        "border border-[#E5E5E5] rounded-[4px] px-[16px] py-[12px] flex flex-col md:gap-[12px] gap-[8px] w-full min-h-[118px] cursor-pointer",
        className,
      )}
      onClick={() => {
        onCardClick?.();
        handleClick();
      }}
    >
      <div className="grid grid-cols-[42px_1fr] gap-[12px]">
        <BFLImage
          src={resolvedLogoSrc}
          alt="stock logo"
          width={36}
          height={36}
          className="w-[42px] h-[42px] pt-[3px] pl-[3px] bg-white rounded-full object-cover"
        />
        <div className="text-[14px] md:text-[16px] font-[400px] text-[#1a1a1a]">
          {heading}
        </div>
        <div className="col-start-2 flex w-full items-end gap-2">
          <div className="flex min-w-0 flex-1 items-end justify-start">
            {symbol ? (
              <div
                className={`flex w-fit max-w-full items-center gap-[4px] rounded-[99px] px-[8px] py-[6px] ${
                  isPositive
                    ? "bg-[#CEEAE5] border border-[#99D8CF]"
                    : "bg-[#F4D8cd] border border-[#E8B6A2]"
                }`}
              >
              <svg
                className="h-3 w-3 shrink-0 md:h-4 md:w-4"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4.5C12 4.10218 11.842 3.72064 11.5607 3.43934C11.2794 3.15804 10.8978 3 10.5 3H9C9 2.20435 8.68393 1.44129 8.12132 0.87868C7.55871 0.31607 6.79565 0 6 0C5.20435 0 4.44129 0.31607 3.87868 0.87868C3.31607 1.44129 3 2.20435 3 3H1.5C1.10218 3 0.720644 3.15804 0.43934 3.43934C0.158035 3.72064 0 4.10218 0 4.5L0 9.5C0.000793929 10.1628 0.264441 10.7982 0.73311 11.2669C1.20178 11.7356 1.8372 11.9992 2.5 12H9.5C10.1628 11.9992 10.7982 11.7356 11.2669 11.2669C11.7356 10.7982 11.9992 10.1628 12 9.5V4.5ZM4 3C4 2.46957 4.21071 1.96086 4.58579 1.58579C4.96086 1.21071 5.46957 1 6 1C6.53043 1 7.03914 1.21071 7.41421 1.58579C7.78929 1.96086 8 2.46957 8 3H4Z"
                  fill={isPositive ? "#067161" : "#B40000"}
                />
              </svg>
              <p
                className={twMerge(
                  "md:text-[12px] text-[10px] font-[400px] text-[#1a1a1a]",
                  paragraphClassName,
                  symbolClassName,
                )}
              >
                {symbol}
              </p>
              <p
                className={twMerge(
                  "md:text-[12px] text-[10px] font-[400px]",
                  paragraphClassName,
                  isPositive ? "text-[#067161]" : "text-[#B40000]",
                )}
              >
                {isPositive ? "+" : ""}
                {perChange.toFixed(2)}%
              </p>
              </div>
            ) : null}
          </div>
          <p
            className={twMerge(
              "shrink-0 text-right md:text-[12px] text-[10px] font-[400px] text-[#1a1a1a]",
              paragraphClassName,
            )}
          >
            {formatTimestamp(createdAt, timeLabels)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LatestFinancialCard;
